import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface HealthStatus {
  status: string;
  checks: Record<string, { status: string; latency?: number }>;
}

export default function SettingsPage() {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadHealthStatus();
  }, []);

  const loadHealthStatus = async () => {
    try {
      const response = await api.get<HealthStatus>('/health/ready');
      setHealthStatus(response.data || null);
    } catch (error) {
      console.error('Failed to load health status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-white">Settings</h1>
        <p className="mt-1 text-surface-400">
          Configure integrations and manage your platform settings
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* OpenAI Configuration */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-white">OpenAI</h2>
            <StatusIndicator
              status={healthStatus?.checks?.openai?.status || 'unknown'}
            />
          </div>
          <div className="card-body space-y-4">
            <p className="text-sm text-surface-400">
              OpenAI API key is configured via environment variables for security.
              The key is never exposed to the client.
            </p>
            <div className="p-4 rounded-lg bg-surface-800 border border-surface-700">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/20 text-green-400">
                  <LockIcon />
                </div>
                <div>
                  <p className="text-sm font-medium text-surface-200">Server-side only</p>
                  <p className="text-xs text-surface-500">
                    Set <code className="text-primary-400">OPENAI_API_KEY</code> in your environment
                  </p>
                </div>
              </div>
            </div>
            <div className="text-xs text-surface-500">
              Model: <code className="text-surface-400">gpt-4o-realtime-preview</code>
            </div>
          </div>
        </div>

        {/* LiveKit Configuration */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-white">LiveKit</h2>
            <StatusIndicator
              status={healthStatus?.checks?.livekit?.status || 'unknown'}
            />
          </div>
          <div className="card-body space-y-4">
            <p className="text-sm text-surface-400">
              LiveKit credentials are configured via environment variables.
              Access tokens are generated server-side.
            </p>
            <div className="space-y-2">
              <EnvVarRow name="LIVEKIT_SERVER_URL" description="WebSocket server URL" />
              <EnvVarRow name="LIVEKIT_API_KEY" description="API key for authentication" />
              <EnvVarRow name="LIVEKIT_API_SECRET" description="API secret (never exposed)" />
            </div>
          </div>
        </div>

        {/* GHL Configuration */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-white">GoHighLevel (GHL)</h2>
            <StatusIndicator
              status={healthStatus?.checks?.ghl?.status || 'unknown'}
            />
          </div>
          <div className="card-body space-y-4">
            <p className="text-sm text-surface-400">
              GHL MCP integration for CRM operations. Uses Private Integration Token (PIT)
              for authentication.
            </p>
            <div className="space-y-2">
              <EnvVarRow name="GHL_BASE_URL" description="API base URL" />
              <EnvVarRow name="GHL_PIT_TOKEN" description="Private Integration Token" />
              <EnvVarRow name="GHL_LOCATION_ID" description="Optional location ID header" />
            </div>
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-xs text-yellow-400">
                <strong>Note:</strong> When credentials are not configured, the system uses a mock
                adapter for development and testing.
              </p>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-display text-lg font-semibold text-white">System Status</h2>
          </div>
          <div className="card-body">
            {isLoading ? (
              <p className="text-surface-500">Loading...</p>
            ) : healthStatus ? (
              <div className="space-y-3">
                {Object.entries(healthStatus.checks).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-3 rounded-lg bg-surface-800"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-surface-300 capitalize">
                        {key.replace('_', ' ')}
                      </span>
                      {value.latency !== undefined && (
                        <span className="text-xs text-surface-500">{value.latency}ms</span>
                      )}
                    </div>
                    <StatusIndicator status={value.status} />
                  </div>
                ))}
                <button
                  onClick={loadHealthStatus}
                  className="btn-secondary w-full mt-4"
                >
                  Refresh Status
                </button>
              </div>
            ) : (
              <p className="text-red-400">Failed to load status</p>
            )}
          </div>
        </div>

        {/* Data Hygiene */}
        <div className="card lg:col-span-2">
          <div className="card-header">
            <h2 className="font-display text-lg font-semibold text-white">Data Hygiene Strategy</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-surface-200 mb-2">Living Summary Notes</h3>
                <p className="text-sm text-surface-400 mb-3">
                  Instead of creating custom fields, the system maintains a single "AI SUMMARY v1"
                  note per contact that is updated with each interaction.
                </p>
                <ul className="text-sm text-surface-500 space-y-1">
                  <li>• Contact profile and preferences</li>
                  <li>• Qualification data with confidence ranges</li>
                  <li>• Interaction history summary</li>
                  <li>• Next steps and action items</li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-medium text-surface-200 mb-2">Interaction Logs</h3>
                <p className="text-sm text-surface-400 mb-3">
                  Each conversation appends a separate interaction note with session details,
                  preserving the full history while keeping the summary clean.
                </p>
                <ul className="text-sm text-surface-500 space-y-1">
                  <li>• Conversation ID and channel</li>
                  <li>• Duration and outcome</li>
                  <li>• Key points discussed</li>
                  <li>• Session summary</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusIndicator({ status }: { status: string }) {
  const statusConfig: Record<string, { color: string; label: string }> = {
    healthy: { color: 'bg-accent-500', label: 'Healthy' },
    configured: { color: 'bg-accent-500', label: 'Configured' },
    not_configured: { color: 'bg-yellow-500', label: 'Not Configured' },
    unhealthy: { color: 'bg-red-500', label: 'Unhealthy' },
    unknown: { color: 'bg-surface-500', label: 'Unknown' },
  };

  const config = statusConfig[status] || statusConfig.unknown;

  return (
    <div className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${config.color}`} />
      <span className="text-xs text-surface-400">{config.label}</span>
    </div>
  );
}

function EnvVarRow({ name, description }: { name: string; description: string }) {
  return (
    <div className="flex items-center justify-between p-2 rounded bg-surface-800">
      <code className="text-xs text-primary-400">{name}</code>
      <span className="text-xs text-surface-500">{description}</span>
    </div>
  );
}

function LockIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}




